import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

/**
 * AuthProvider — dual auth: Azure AD SSO (employees) + Azure B2C (customers)
 *
 * LOCAL LOGIN: Uses demoUsers.js for demo/test login gate (same pattern as
 * the Bit Annotator app). Persists session in localStorage so page refresh
 * doesn't require re-login.
 *
 * PRODUCTION: Will be replaced with MSAL (@azure/msal-browser) for real
 * Azure AD SSO and B2C authentication flows.
 */
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userType, setUserType] = useState(null);
  const [account, setAccount] = useState(null);
  const [permissions, setPermissions] = useState([]);

  // Restore session from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('mrt_currentUser');
      if (saved) {
        const user = JSON.parse(saved);
        setIsAuthenticated(true);
        setUserType(user.roleKey || 'guest');
        setAccount(user);
        setPermissions(
          JSON.parse(localStorage.getItem('mrt_permissions') || '[]')
        );
      }
    } catch {
      // Corrupted localStorage — ignore
    }
  }, []);

  /**
   * Local login — called from LoginPage after authenticateLocal() succeeds.
   * @param {Object} user - demoUser object (name, email, roleKey, account, etc.)
   * @param {string[]} perms - permissions array from the demo user config
   */
  const loginLocal = useCallback((user, perms = []) => {
    setIsAuthenticated(true);
    setUserType(user.roleKey || 'guest');
    setAccount(user);
    setPermissions(perms);
    localStorage.setItem('mrt_currentUser', JSON.stringify(user));
    localStorage.setItem('mrt_permissions', JSON.stringify(perms));
  }, []);

  /**
   * SSO login placeholder — will be replaced with MSAL acquireTokenSilent / redirect
   */
  const login = useCallback(async (tokenOrUser) => {
    // TODO: MSAL integration
    console.log('[AuthContext] SSO login placeholder', tokenOrUser);
  }, []);

  /**
   * Logout — clears state and localStorage
   */
  const logout = useCallback(() => {
    setIsAuthenticated(false);
    setUserType(null);
    setAccount(null);
    setPermissions([]);
    localStorage.removeItem('mrt_currentUser');
    localStorage.removeItem('mrt_permissions');
  }, []);

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      userType,
      account,
      permissions,
      login,
      loginLocal,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export default AuthContext;
